import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import SourceTypeChip from "./SourceTypeChip";
import {
  AccordionDetails,
  AccordionSummary,
  Anchor,
} from "../../../../components";
import classes from "./CrmComp.module.css";
import IconComponent from "../../../../components/Icon/IconComponent";
import { GREY } from "../../../../utils/constants/colors";
import {
  ICON_LABELS,
  fontSize,
  fontWeight,
} from "../../../../utils/constants/UI";
import IconColoredComponent from "../../../../components/Icon/IconColoredComponent";
import TypographyInline from "../../../../components/Typography/TypographyInline";
import { STRINGS } from "../../strings";
import {
  dateTimeStringEmailLog,
  dateOnlyStringEmailLog,
  decompressString,
  removeStylesFromHeadHtml,
  stripUnsubscribeBlockForDisplay,
} from "../../../../utils/helpers";
import { Accordion } from "@mui/material";
import { Button } from "../../../../components/Buttons";
import AddComment from "../../Components/AddComment";
import { crmEndpoints } from "../../../../utils/constants/httpConstants";
import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest,
} from "../../../../Apis";
import { useNavigate } from "react-router-dom";
import EventEmitter from "../../../../utils/emitter/EventEmitter";
import { EMITTER_ERROR, EMITTER_SUCCESS } from "../../../../utils/constants";
import DeleteCommentModal from "../../Components/DeleteCommentModal";
import parse from "html-react-parser";
import CustomModal from "../../../../components/Modals/CustomModal";
import {
  PIN_ICON,
  PUSH_PIN_ICON,
  UNPIN_ICON,
} from "../../../../utils/constants/assets";
import Image from "../../../../components/Image/Image";
import CustomTooltip from "../../../../components/Tooltip/CustomTooltip";

// Campaign-driven email bodies often arrive as plain text (line breaks +
// "•" bullets + "Heading:" sections). parse() of raw text collapses
// whitespace and renders it as one giant paragraph. Detect bodies that have
// no real HTML markup and convert them into structured HTML so paragraphs,
// bullets and section headings survive in the timeline preview.
const __escapeBodyHtml = (s) =>
  String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const __linkifyBody = (s) =>
  s.replace(
    /(https?:\/\/[^\s<>"]+)/g,
    (url) => `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`,
  );

// `decodeURIComponent` throws `URIError: URI malformed` whenever the input
// contains a `%` followed by invalid hex (e.g. a literal `%` in plain HTML
// that was never URI-encoded in the first place). Backend bodies sometimes
// come through already-decoded, so we fall back to the raw string when
// decoding fails. Without this guard the entire EmailSentList component
// crashes on render.
const safeDecodeURIComponent = (s) => {
  if (typeof s !== "string") return s;
  try {
    return decodeURIComponent(s);
  } catch (_) {
    return s;
  }
};

const formatPlainTextEmailBody = (input) => {
  if (typeof input !== "string") return input;
  // Already-HTML emails (from Gmail / rich editor) keep their formatting.
  if (/<[a-zA-Z][^>]*>/.test(input)) return input;

  const text = input.replace(/\r\n/g, "\n").trim();
  if (!text) return "";

  // Inline bullets like "... support • MES integration • Ongoing data" - split
  // them onto their own lines so the bullet pass below picks them up.
  const exploded = text.replace(/\s+•\s+/g, "\n• ");

  const blocks = exploded.split(/\n{2,}/);
  const renderBlock = (block) => {
    const lines = block.split("\n").map((l) => l.trim()).filter(Boolean);
    if (!lines.length) return "";
    const isBullet = (l) => /^[•\-*]\s+/.test(l);

    if (lines.every(isBullet)) {
      return `<ul>${lines
        .map(
          (l) =>
            `<li>${__linkifyBody(__escapeBodyHtml(l.replace(/^[•\-*]\s+/, "")))}</li>`,
        )
        .join("")}</ul>`;
    }

    const parts = [];
    let bullets = [];
    const flush = () => {
      if (bullets.length) {
        parts.push(
          `<ul>${bullets
            .map((b) => `<li>${__linkifyBody(__escapeBodyHtml(b))}</li>`)
            .join("")}</ul>`,
        );
        bullets = [];
      }
    };
    lines.forEach((l) => {
      if (isBullet(l)) {
        bullets.push(l.replace(/^[•\-*]\s+/, ""));
        return;
      }
      flush();
      const m = l.match(/^([A-Z][A-Za-z ]{1,40}):\s*(.*)$/);
      if (m) {
        parts.push(
          m[2]
            ? `<p><strong>${__escapeBodyHtml(m[1])}:</strong> ${__linkifyBody(
                __escapeBodyHtml(m[2]),
              )}</p>`
            : `<p><strong>${__escapeBodyHtml(m[1])}:</strong></p>`,
        );
      } else {
        parts.push(`<p>${__linkifyBody(__escapeBodyHtml(l))}</p>`);
      }
    });
    flush();
    return parts.join("");
  };

  return blocks.map(renderBlock).join("");
};

// Single body pipeline for both render branches (thread view and single mail).
// Compressed bodies must be inflated + decoded BEFORE the unsubscribe footer can
// be matched, so the strip sits after that and before the plain-text formatter.
const renderableEmailBody = (body, isCompressed, domId) => {
  const raw = isCompressed
    ? safeDecodeURIComponent(
      decompressString(removeStylesFromHeadHtml(body, domId)),
    )
    : removeStylesFromHeadHtml(body, domId);
  return formatPlainTextEmailBody(stripUnsubscribeBlockForDisplay(raw));
};

const EmailSentList = ({
  emailData,
  contactDetails,
  id,
  getEmailLogList = () => { },
  expanded,
  setOpenSendEmailModal = () => { },
  setClickedEmail = () => { },
  setReplyType = () => { },
  showOperation = true,
  // True for campaign emails whose parent campaign is still in
  // Ready To Run / draft / queued - the touch has no real send time yet,
  // so we hide the clock + date in the header to avoid implying one.
  isPreScheduled = false,
}) => {
  const navigate = useNavigate();
  const [emailOpen, setEmailOpen] = useState(false);

  // Mirror the "Call logged by X" / "Task created by X" header pattern used
  // by ListingTaskCard. Looks up the user who created/sent this email so
  // we can render "Email logged by …" / "Email sent by …" next to the
  // subject in the collapsed header.
  const tenantUsersListSelector = useSelector(
    (state) => state?.tenantUsersList?.payload?.tenant_user_list?.data?.data,
  );
  const senderRecord = tenantUsersListSelector?.find(
    (u) => u?._id === emailData?.user_id,
  );
  const senderName =
    senderRecord?.first_name || senderRecord?.last_name
      ? `${senderRecord?.first_name || ""} ${senderRecord?.last_name || ""}`.trim()
      : "";
  // Header verb. The backend hands us a precise `activity_title` per record
  // ("Email Scheduled by …", "Email sent by …", "Unenrolled email"), which
  // already embeds the sender name. Use it verbatim when present so each
  // email reads correctly instead of a blanket "Logged Email". When the
  // title carries the actor we suppress the separate "by X" below to avoid
  // duplicating the name. Sales-sequence mails arrive without an
  // activity_title, so fall back to a status/type-derived verb.
  // Header verb. The backend hands us a precise `activity_title` per record
  // ("Email Scheduled by …", "Email sent by …", "Unenrolled email"), which
  // already embeds the sender name. Use it verbatim when present - both on
  // the standalone Emails tab and inside the timeline embed - so the inner
  // card reads the same as the outer timeline row. Sales-sequence mails
  // arrive without an activity_title, so fall back to a status/type-derived
  // verb.
  const apiActivityTitle = emailData?.activity_title?.trim();
  const deriveFallbackVerb = () => {
    const status = String(emailData?.status || "").toLowerCase();
    const rawType = String(emailData?.type || "").toLowerCase();
    if (status === "scheduled") return STRINGS.EMAIL_SCHEDULED_LABEL;
    if (status === "error") return "Failed to Send";
    if (rawType === "campaign_mail_unenrolled" || status === "unenrolled")
      return "Unenrolled Email";
    if (status === "sent" || rawType === "sales_sequence_mail")
      return "Sent Email";
    return "Logged Email";
  };
  // A campaign still in Ready To Run has no send date at all, so the stored
  // "Email Scheduled by X" title over-promises — it reads as if a time exists.
  // Those rows say "Email Schedule Pending" until the campaign is scheduled.
  // Second signal: the row itself has no processing time. The campaign flow leaves
  // processing_date_time null until schedule time, and the parent-campaign lookup
  // that drives `isPreScheduled` misses whenever the campaign's activity rows were
  // stamped under a different contact_id than the contact being viewed.
  const emailRowStatus = String(emailData?.status || "").toLowerCase();
  // Campaign and sales-sequence mails are both engine-queued; declared here (ahead
  // of the header verb) because the verb and the timestamp masking both need it.
  const isAutomatedEmail = ["marketing campaign", "sales sequence"].includes(
    String(emailData?.record_type_for_crm || "").trim().toLowerCase(),
  );
  const isSchedulePending =
    isPreScheduled ||
    (isAutomatedEmail &&
      !emailData?.processing_date_time &&
      !emailData?.sent_date_time &&
      !emailData?.unenrolled &&
      !["sent", "unenrolled", "error"].includes(emailRowStatus));
  const emailVerb = isSchedulePending
    ? STRINGS.EMAIL_SCHEDULE_PENDING
    : apiActivityTitle || deriveFallbackVerb();
  // Connector before the contact name. Anything not yet delivered reads "with".
  const isNotYetSent =
    isSchedulePending ||
    [
      "scheduled",
      "pending",
      "active",
      "queued",
      "pause",
      "paused",
      // Parked because the contact moved to a sales sequence — it will never send,
      // so it was never sent "to" anyone.
      "suppressed",
    ].includes(emailRowStatus);
  // A reply came FROM the contact, so "to <Contact>" reads backwards on it. The backend marks the
  // row it synthesizes from the campaign's reply fields, and only that row flips the connector.
  const isInboundReply = !!emailData?.is_reply_row;
  const receiverConnector = isInboundReply
    ? STRINGS.FROM_LOWER
    : isNotYetSent
      ? STRINGS.WITH
      : STRINGS.TO;
  // Paused / cancelled / unenrolled are states the verb cannot express on its own.
  // Everything else (scheduled, sent, plain queued) is already carried by the verb,
  // so no pill is rendered for those - a pill on every row would be noise.
  const statusPill = (() => {
    if (["pause", "paused"].includes(emailRowStatus))
      return { label: "Paused", bg: "#FFF6E5", color: "#B25E09", border: "#FBE2BE" };
    if (emailRowStatus === "suppressed")
      return { label: "Cancelled", bg: "#F1F1F2", color: "#5F5F61", border: "#E5E5E6" };
    if (emailRowStatus === "unenrolled" || emailData?.unenrolled === true)
      return { label: "Unenrolled", bg: "#F1F1F2", color: "#5F5F61", border: "#E5E5E6" };
    return null;
  })();
  // What the contact DID with this mail. The backend sends `engagement` three-valued per signal:
  // true when a person did it, null when the pixel/link fired but it cannot be attributed to one
  // (a mail-security scanner, or a row from before open labelling existed). Only `true` earns a
  // chip - a maybe-a-bot chip is worse than no chip, because a rep acts on it.
  // Ordered weakest signal first so the eye finishes on the reply.
  const engagementChips = (() => {
    const e = emailData?.engagement;
    if (!e) return [];
    const chips = [];
    if (e.opened === true)
      chips.push({ label: "Opened", at: e.opened_at, bg: "#F1F1F2", color: "#5F5F61", border: "#E5E5E6" });
    if (e.clicked === true)
      chips.push({ label: "Clicked", at: e.clicked_at, bg: "#EAF1FF", color: "#2C5DE5", border: "#C9D9FC" });
    if (e.replied)
      chips.push({ label: "Replied", at: e.replied_at, bg: "#E7F6EC", color: "#14804A", border: "#C3E5D0" });
    if (e.bounced)
      chips.push({ label: "Bounced", at: e.bounced_at, bg: "#FDECEC", color: "#B42318", border: "#F8CDCB" });
    return chips;
  })();

  // When the verb came from the API it already says "… by <name>", so don't
  // render the separate "by senderName" chunk again.
  const verbIncludesActor = !!apiActivityTitle && !isSchedulePending;
  const [emailComments, setEmailComments] = useState([]);
  const [emailCommentsPage, setEmailCommentsPage] = useState(0);
  const [commentBody, setCommentBody] = useState("");
  const [createCommentLoading, setCreateCommentLoading] = useState(false);
  const [isDeleteCommentLoading, setIsDeleteCommentLoading] = useState(false);
  const [deleteCommentModalOpen, setDeleteCommentModalOpen] = useState(false);
  const [clickedCommentData, setClickedCommentData] = useState({});
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteButtonLoading, setDeleteButtonLoading] = useState(false);
  const [clickedViewThread, setClickedViewThread] = useState(false);
  const [viewThreadLoading, setViewThreadLoading] = useState(false);
  const [emailThreadData, setEmailThreadData] = useState({});

  const emailNamesDisplayFromArray = (data) => {
    let emailsLabel = "";
    data?.forEach((element, index) => {
      emailsLabel = `${emailsLabel} ${element}${data?.length - 1 === index ? "" : ","
        }`;
    });
    return emailsLabel;
  };

  // delete mail modal handler
  const deleteEmailHandler = () => {
    setDeleteModal(true);
  };

  // delte mail api handler
  const deleteMailApiHandler = async () => {
    try {
      let url = "";
      if (clickedViewThread && emailThreadData?.length) {
        url = `${crmEndpoints.deleteEmail}/${emailData?.message_id}`;
      } else {
        url = `${crmEndpoints.deleteThread}/${emailData?.thread_id}`;
      }
      setDeleteButtonLoading(true);
      const response = await deleteRequest(url, {}, navigate);
      EventEmitter.emit(EMITTER_SUCCESS, response?.data?.message);
      getEmailLogList();
      setDeleteModal(false);
      setDeleteButtonLoading(false);
    } catch (error) {
      setDeleteModal(false);
      setDeleteButtonLoading(false);
      EventEmitter.emit(EMITTER_ERROR, error?.data?.message);
      console.log("Error ->>>> ", error);
    }
  };

  // reply to mail api handler
  const replyEmailHandler = (type) => {
    setReplyType(type);
    setClickedEmail(emailData);
    setOpenSendEmailModal(true);
  };

  // forward mail api handler
  const forwardEmailHandler = () => {
    setClickedEmail(emailData);
  };

  // pin email log api handler
  const emailPinHandler = async (data) => {
    try {
      // setLogEmailCommentsPage(0);
      let payload = {
        pin: emailData?.pin ? 0 : 1,
        _id: emailData?._id,
      };
      const response = await putRequest(
        navigate,
        crmEndpoints.pinEmail,
        payload
      );
      getEmailLogList();
      EventEmitter.emit(EMITTER_SUCCESS, response?.data?.message);
    } catch (error) {
      EventEmitter.emit(EMITTER_ERROR, error?.data?.message);
      console.log("Error ->>>> ", error);
    }
  };

  // api call for getting email log comments
  const getEmailComments = async (isOnScroll, initial) => {
    try {
      let url =
        crmEndpoints.getEmailLogComments +
        `/${emailData?._id}?size=10&page=${initial ? 0 : emailCommentsPage}`;
      const response = await getRequest(navigate, url);
      if (isOnScroll) {
        setEmailComments([...emailComments, ...response?.data?.data?.records]);
      } else {
        setEmailComments([...response?.data?.data?.records]);
      }
    } catch (error) {
      EventEmitter.emit(EMITTER_ERROR, error?.data?.message);
      console.log("Error ->>>> ", error);
    }
  };

  // creating email log comment api handler
  const createCommentHandler = async () => {
    try {
      setEmailCommentsPage(0);
      let payload = {
        comment: commentBody,
        email_id: emailData?._id,
      };
      setCreateCommentLoading(true);
      const response = await postRequest(
        navigate,
        crmEndpoints.createEmailLogComment,
        payload
      );
      getEmailComments(false, true);

      EventEmitter.emit(EMITTER_SUCCESS, response?.data?.message);
      setCreateCommentLoading(false);
    } catch (error) {
      EventEmitter.emit(EMITTER_ERROR, error?.data?.message);
      console.log("Error ->>>>> ", error);
    }
  };

  // edit email log comment api handler
  const updateEmailLogCommentHandler = async (comment, editValue) => {
    try {
      setEmailCommentsPage(0);
      let payload = {
        comment: editValue[comment?.id],
        id: comment?.id,
      };
      const response = await putRequest(
        navigate,
        crmEndpoints.updateEmailLogComment,
        payload
      );
      getEmailComments(false, true);
      EventEmitter.emit(EMITTER_SUCCESS, response?.data?.message);
    } catch (error) {
      EventEmitter.emit(EMITTER_ERROR, error?.data?.message);
      console.log("Error ->>>> ", error);
    }
  };

  // delete email log comment api handler
  const deleteEmailLogCommentHandler = async (data) => {
    try {
      setEmailCommentsPage(0);
      setIsDeleteCommentLoading(true);
      let url = `${crmEndpoints?.deleteEmailLogComment}/${data?.id}`;
      const response = await deleteRequest(url, {}, navigate);
      getEmailComments(false, true);
      setIsDeleteCommentLoading(false);
      setDeleteCommentModalOpen(false);
      EventEmitter.emit(EMITTER_SUCCESS, response?.data?.message);
    } catch (error) {
      EventEmitter.emit(EMITTER_ERROR, error?.data?.message);
      console.log("Error ->>>> ", error);
    }
  };

  // storing clicked data for delete and open delete modal.
  const deleteEmailLogCommentModalHandler = (data) => {
    setClickedCommentData(data);
    setDeleteCommentModalOpen(true);
  };

  // view all thread api handler
  const viewThreadApiHandler = async () => {
    try {
      setViewThreadLoading(true);
      let url = `${crmEndpoints.getEmailByThreads}/${emailData?.thread_id}`;
      const response = await getRequest(navigate, url);
      setEmailThreadData(response?.data?.data?.records);
      setViewThreadLoading(false);
      setClickedViewThread(true);
      // EventEmitter.emit(EMITTER_SUCCESS, response?.data?.message);
    } catch (error) {
      console.log("Error -->>>>>> ", error);
      setViewThreadLoading(false);
      EventEmitter.emit(EMITTER_ERROR, error?.data?.message);
    }
  };

  // --- Row-level action gating (bug fixes) --------------------------------
  // Reply only works for a genuine manual email that was actually SENT (it
  // needs a real message_id for the provider reply lookup). Marketing
  // Campaign / Sales Sequence rows are merged in for display only, carry
  // message_id: null, and previously produced "Invalid data provided" on
  // reply. Delete only reaches the CRM email indices, so campaign / sequence
  // rows (whose id lives in a foreign index) previously returned "Record not
  // found". We use the NEGATIVE record-type list (not `=== 'Manual'`) because
  // the company/deals email endpoints don't stamp record_type_for_crm — a
  // positive check would wrongly hide these actions on Account & Deals.
  const isCampaignOrSequenceEmail = ["Marketing Campaign", "Sales Sequence"].includes(
    emailData?.record_type_for_crm
  );
  const isReplyable =
    emailData?.status === "sent" &&
    !!emailData?.message_id &&
    !isCampaignOrSequenceEmail;
  const isDeletable = !isCampaignOrSequenceEmail;
  // Marketing Campaign sends go out at randomized times, so the scheduled time is
  // misleading — show only the date until the mail is actually SENT, then show the
  // real send time. (bug #10, extended from the timeline to the Emails section.)
  // Sales Sequence sends are queued the same way, so they get the same treatment:
  // no clock time until the mail has actually gone out. (`isAutomatedEmail` is
  // declared with the header-verb block above.)
  const hideCampaignTime =
    isAutomatedEmail &&
    String(emailData?.status || "").toLowerCase() !== "sent";
  const emailTimeLabel = (ts) =>
    hideCampaignTime ? dateOnlyStringEmailLog(ts) : dateTimeStringEmailLog(ts);

  // A sent automated mail must show the moment it actually went out, not the
  // window it was queued for — the engine picks a randomized time inside that
  // window, so processing_date_time is off by up to hours.
  const headerTimestamp =
    (emailRowStatus === "sent" || emailData?.sent === true
      ? emailData?.sent_date_time
      : null) || emailData?.processing_date_time;

  const sentEmailOperations = [
    {
      id: STRINGS.DELETE_SENT_EMAIL_SMALL,
      label: STRINGS.DELETE,
      leftIcon: ICON_LABELS.DELETE,
      onClick: () => deleteEmailHandler(),
      isVisible: isDeletable,
    },
    {
      id: STRINGS.REPLY_ALL_SMALL,
      label: STRINGS.REPLY_ALL,
      leftIcon: ICON_LABELS.REPLY_ALL,
      onClick: () => replyEmailHandler(STRINGS.REPLY_ALL_SMALL),
      isVisible: isReplyable,
    },
    {
      id: STRINGS.REPLY_SMALL,
      label: STRINGS.REPLY,
      leftIcon: ICON_LABELS.REPLY,
      onClick: () => replyEmailHandler(STRINGS.REPLY_SMALL),
      isVisible: isReplyable,
    },
    {
      id: STRINGS.FORWARD_SMALL,
      label: STRINGS.FORWARD,
      leftIcon: ICON_LABELS.SHORTCUT,
      onClick: () => forwardEmailHandler(),
      isVisible: false,
    },
  ];

  const deleteModalHeader = {
    heading: {
      id: STRINGS.DELETE_MODAL_COMMENT,
      label: STRINGS.DELETE_EMAIL_HEADER_TILE,
    },
  };

  const deleteModalRightFooter = (
    <div className={classes.modalFooterRightData}>
      <Button
        label={STRINGS.CANCEL}
        variant={"ghost"}
        size={"sm36"}
        onClick={() => setDeleteModal(false)}
      />
      <Button
        label={STRINGS.DELETE}
        variant={"primary"}
        size={"sm36"}
        onClick={() => {
          deleteMailApiHandler();
        }}
        isLoading={deleteButtonLoading}
      />
    </div>
  );
  const deleteModalLeftFooter = <div></div>;

  useEffect(() => {
    if (expanded) {
      setEmailOpen(expanded);
    }
  }, [emailData]);

  useEffect(() => {
    if (emailOpen) {
      getEmailComments(false, true);
    }
  }, [emailOpen]);

  const random_id = "log" + Date.now();

  return (
    <div key={id}>
      <Accordion
        className={classes.accrodionRoot}
        onChange={() => setEmailOpen(!emailOpen)}
        expanded={emailOpen}
      >
        <AccordionSummary className={classes.accordionSummary}>
          {/* Pin indicator hidden per UX request. */}
          {/* {showOperation && emailData?.pin ? (
            <div className={classes.pinContainer}>
              <Image className={classes.pinIcon} src={PIN_ICON} />
            </div>
          ) : null} */}
          <div className={classes.summaryContainer}>
            {/* accordion summary left part having title and icons */}
            <div className={classes.emailLogAccordionSummaryLeftPart}>
              {/* Accordion Icon and Email Icon */}
              <div className={classes.accordionSummaryIconContainer}>
                <IconComponent
                  color={GREY.TERTIARY}
                  fontSize={fontSize.DXS}
                  iconLabel={
                    emailOpen
                      ? ICON_LABELS.ACTION_ARROW
                      : ICON_LABELS.KEYBOARD_ARROW_RIGHT
                  }
                />

                <IconColoredComponent
                  variant={"orange"}
                  iconLabel={ICON_LABELS.EMAIL}
                  size={"small"}
                />
              </div>

              {/*
                Header matches the EmailLogList ("Logged Email by X to Y")
                shape:
                  Row 1: "Sent Email" + "by X" + "to <First Last>" + chip
                  Row 2: Subject of the email
              */}
              <div className={classes.loggedEmailHeaderColumn}>
                <div className={classes.loggedEmailTitleContainer}>
                  <TypographyInline
                    color={GREY.PRIMARY}
                    label={emailVerb}
                    size={"sm"}
                    fontWeight={fontWeight.SEMIBOLD}
                    isNoWrap
                  />
                  {!verbIncludesActor && senderName && (
                    <TypographyInline
                      color={GREY.SECONDARY}
                      label={`${STRINGS.BY} ${senderName}`}
                      size={"sm"}
                      fontWeight={fontWeight.REGULAR}
                      isNoWrap
                    />
                  )}
                  {(contactDetails?.first_name || contactDetails?.last_name) && (
                    <TypographyInline
                      color={GREY.PRIMARY}
                      // A mail that has not gone out yet was not sent "to" anyone -
                      // it is scheduled "with" them. Sent / logged rows keep "to".
                      label={`- ${receiverConnector} ${contactDetails?.first_name || ""} ${contactDetails?.last_name || ""}`.trim()}
                      size={"sm"}
                      fontWeight={fontWeight.SEMIBOLD}
                      isEllipses
                    />
                  )}
                  {/*
                    Source pill (Sales Sequence / Marketing Campaign /
                    Manual). Reads `record_type_for_crm` straight off the
                    record so all three variants surface consistently. Only
                    shown on the standalone Emails tab - inside the timeline
                    detail (showOperation === false) the timeline row owns
                    the pill itself, so we suppress it here.
                  */}
                  {/* Lifecycle pill for engine-driven rows. The verb alone could not say
                      a campaign had been PAUSED - the row kept reading "Email Scheduled"
                      while the campaign was stopped, so the Emails tab disagreed with the
                      timeline chip. Only shown when there is something other than the
                      normal scheduled/sent flow to report. */}
                  {showOperation && statusPill && (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        height: "20px",
                        padding: "0 10px",
                        borderRadius: "999px",
                        fontSize: "11.5px",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        background: statusPill.bg,
                        color: statusPill.color,
                        border: `1px solid ${statusPill.border}`,
                      }}
                    >
                      {statusPill.label}
                    </span>
                  )}
                  {showOperation &&
                    engagementChips.map((chip) => (
                      <span
                        key={chip.label}
                        title={chip.at ? new Date(Number(chip.at)).toLocaleString() : undefined}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          height: "20px",
                          padding: "0 10px",
                          borderRadius: "999px",
                          fontSize: "11.5px",
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                          background: chip.bg,
                          color: chip.color,
                          border: `1px solid ${chip.border}`,
                        }}
                      >
                        {chip.label}
                      </span>
                    ))}
                  {showOperation && (
                    <SourceTypeChip
                      recordType={(() => {
                        // Prefer the explicit field; fall back to the
                        // record's `type` for older payloads that haven't
                        // been migrated yet.
                        if (emailData?.record_type_for_crm)
                          return emailData.record_type_for_crm;
                        const rawType = String(
                          emailData?.type || "",
                        ).toLowerCase();
                        if (rawType === "sales_sequence_mail")
                          return "Sales Sequence";
                        if (rawType === "campaign_mail")
                          return "Marketing Campaign";
                        return null;
                      })()}
                    />
                  )}
                </div>
                {emailData?.subject && (
                  <TypographyInline
                    color={GREY.PRIMARY}
                    label={emailData.subject}
                    size={"sm"}
                    fontWeight={fontWeight.SEMIBOLD}
                    isEllipses
                  />
                )}
              </div>
            </div>

            {/* accordion summary right part having date time and action icon */}
            {/*
              For campaign emails that haven't been sent yet (status pause /
              scheduled / paused) the meaningful timestamp is when the email
              is queued to go out, not when the record was created. Surface
              `processing_date_time` in that case so users see the upcoming
              send time. Sent / logged emails keep showing `created_at`.

              When this card is rendered inside the timeline view
              (showOperation === false), the timeline row already shows the
              date in its own right column, so we suppress this internal
              clock/date pair to avoid duplicate timestamps.

              Also suppress the date entirely for `isPreScheduled` campaign
              emails - the parent campaign is still in Ready To Run / draft /
              queued, so no real send time exists yet.
            */}
            <div className={classes.emailLogAccordionSummaryRightPart}>
              {/*
                Right-side clock + date. Always reads
                `processing_date_time` (the contact date the user picked
                when logging the email / the send window for automated
                touches). When that field is missing - e.g. a partial
                payload or a draft - the date row is omitted entirely so
                we don't fall back to a misleading created_at.
              */}
              {showOperation && !isSchedulePending && headerTimestamp && (
                <div className={classes.loggedEmailTimeLabel}>
                  <IconComponent
                    color={GREY.TERTIARY}
                    fontSize={fontSize.MD}
                    iconLabel={ICON_LABELS.SCHEDULE}
                  />
                  <TypographyInline
                    color={GREY.SECONDARY}
                    label={emailTimeLabel(headerTimestamp)}
                    size={"sm"}
                    fontWeight={fontWeight.REGULAR}
                    isEllipses
                  />
                </div>
              )}
              {/* Pin toggle hidden per UX request. */}
              {/* {showOperation && (
                <>
                  <div className={classes.verticalLine} />
                  <div
                    className={classes.moreOptionsIconContianer}
                    onClick={(e) => {
                      e?.stopPropagation();
                    }}
                  >
                    <Image
                      className={classes.pinIcon}
                      src={emailData?.pin ? UNPIN_ICON : PUSH_PIN_ICON}
                      onClick={emailPinHandler}
                    />
                  </div>
                </>
              )} */}
            </div>
          </div>
        </AccordionSummary>
        <AccordionDetails>
          <div className={classes.emailSentAccordionDetail}>
            {/* this will check the length of thread, i.e, this will display when we clicked View Thread Button. */}
            {emailData?.thread_id && emailThreadData?.length ? (
              <>
                {/* mapping all thread data. */}
                {emailThreadData?.map((emailData, index) => {
                  return (
                    <div
                      className={`${classes.eachThreadContainer} ${emailThreadData?.length - 1 !== index
                        ? classes.eachThreadBorder
                        : ""
                        }`}
                    >
                      <div className={classes.recipntsAndOperationsButton}>
                        {/* Accordion Detail to, cc, bcc part */}
                        <div className={classes.emailRecipentsNames}>
                          <TypographyInline
                            color={GREY.SECONDARY}
                            label={emailData?.from_email || ""}
                            size={"sm"}
                            fontWeight={fontWeight.SEMIBOLD}
                            isNoWrap
                          />
                          <TypographyInline
                            color={GREY.SECONDARY}
                            label={`${STRINGS.TO} ${emailNamesDisplayFromArray(
                              emailData?.to_emails
                            )}`}
                            size={"sm"}
                            fontWeight={fontWeight.REGULAR}
                            isNoWrap
                          />
                          {emailData?.cc_emails?.length ? (
                            <TypographyInline
                              color={GREY.SECONDARY}
                              label={`${STRINGS.CC_SMALL
                                } ${emailNamesDisplayFromArray(
                                  emailData?.cc_emails
                                )}`}
                              size={"sm"}
                              fontWeight={fontWeight.REGULAR}
                              isNoWrap
                            />
                          ) : null}
                          {emailData?.bcc_emails?.length ? (
                            <TypographyInline
                              color={GREY.SECONDARY}
                              label={`${STRINGS.BCC_SMALL
                                } ${emailNamesDisplayFromArray(
                                  emailData?.bcc_emails
                                )}`}
                              size={"sm"}
                              fontWeight={fontWeight.REGULAR}
                              isNoWrap
                            />
                          ) : null}
                        </div>
                        <div className={classes.sentEmailOperations}>
                          {sentEmailOperations?.map((item, index) => {
                            return (
                              item.isVisible && (
                                <div key={`sentEmailOeprationsBtn${index}`}>
                                  <Button
                                    LeftIcon={item?.leftIcon}
                                    label={item?.label}
                                    size={"xs28"}
                                    variant={"text"}
                                    isLoading={item?.isLoading}
                                    onClick={() => item?.onClick(emailData)}
                                  />
                                </div>
                              )
                            );
                          })}
                        </div>
                      </div>

                      {/* Body Part. */}
                      <div id="modal_body" className={classes.sentEmailData}>
                        <div id={random_id} className="exclude">
                          {parse(
                            renderableEmailBody(
                              emailData?.body,
                              emailData?.is_compressed,
                              random_id,
                            ),
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </>
            ) : (
              // this will return at initial time whem view thread is not clicked.
              <>
                <div className={classes.recipntsAndOperationsButton}>
                  {/* Accordion Detail to, cc, bcc part */}
                  <div className={classes.emailRecipentsNames}>
                    <TypographyInline
                      color={GREY.SECONDARY}
                      label={emailData?.from_email || ""}
                      size={"sm"}
                      fontWeight={fontWeight.SEMIBOLD}
                      isNoWrap
                    />
                    <TypographyInline
                      color={GREY.SECONDARY}
                      label={`${STRINGS.TO} ${emailNamesDisplayFromArray(
                        emailData?.to_emails
                      )}`}
                      size={"sm"}
                      fontWeight={fontWeight.REGULAR}
                      isNoWrap
                    />
                    {emailData?.cc_emails?.length ? (
                      <TypographyInline
                        color={GREY.SECONDARY}
                        label={`${STRINGS.CC_SMALL
                          } ${emailNamesDisplayFromArray(emailData?.cc_emails)}`}
                        size={"sm"}
                        fontWeight={fontWeight.REGULAR}
                        isNoWrap
                      />
                    ) : null}
                    {emailData?.bcc_emails?.length ? (
                      <TypographyInline
                        color={GREY.SECONDARY}
                        label={`${STRINGS.BCC_SMALL
                          } ${emailNamesDisplayFromArray(emailData?.bcc_emails)}`}
                        size={"sm"}
                        fontWeight={fontWeight.REGULAR}
                        isNoWrap
                      />
                    ) : null}
                  </div>
                  {/* Accordion Detail reply, reply all and forward button part */}
                  {showOperation && (
                    <div className={classes.sentEmailOperations}>
                      {sentEmailOperations?.map((item, index) => {
                        return (
                          item.isVisible && (
                            <div key={`sentEmailOeprationsBtn${index}`}>
                              <Button
                                LeftIcon={item?.leftIcon}
                                label={item?.label}
                                size={"xs28"}
                                variant={"text"}
                                isLoading={item?.isLoading}
                                onClick={() => item?.onClick(emailData)}
                              />
                            </div>
                          )
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Body Part. */}
                <div id="modal_body" className={classes.sentEmailData}>
                  <div id={random_id}>
                    {parse(
                      renderableEmailBody(
                        emailData?.body,
                        emailData?.is_compressed,
                        random_id,
                      ),
                    )}
                  </div>
                </div>

                {/* Attachments list */}
                <div className={classes.attachment}>
                  {emailData?.attachment_links &&
                    emailData?.attachment_links?.map((item) => {
                      return (
                        <CustomTooltip title={item?.name} key={item.url}>
                          <Anchor href={item.url} className={classes.Anchor}>
                            <div className={classes.attachmentContainer}>
                              <IconComponent
                                color={GREY.SECONDARY}
                                fontSize={fontSize.SM}
                                iconLabel={ICON_LABELS.FILE_OPEN}
                              />

                              <TypographyInline
                                color={GREY.SECONDARY}
                                label={
                                  item.name?.slice(0, 30) +
                                  (item?.name?.length > 30 ? "..." : "")
                                }
                                size={"xs"}
                                fontWeight={fontWeight.MEDIUM}
                                isNoWrap
                              />
                            </div>
                          </Anchor>
                        </CustomTooltip>
                      );
                    })}
                </div>
              </>
            )}

            {/* All thread button and email stats */}
            {/* "View Thread" removed (24-Aug #1). Campaign and sequence mail is now grouped into a
                single expandable row, so this button opened a second, competing view of the same
                conversation. Removed from all three screens this time: it was taken off the grouped
                card first, and the tester kept finding it on the plain rows. The empty div stays so
                the scheduled-time label keeps its position in the flex row. */}
            {emailData?.status === "scheduled" ? <div className={classes.threadEmailStatsContainer}>
              <div></div>

              {emailData?.status === "scheduled" ? <div>
                <TypographyInline
                  color={GREY.SECONDARY}
                  label={`${STRINGS.SCHEDULE_FOR} ${emailTimeLabel(emailData?.processing_date_time)}`}
                  size={"sm"}
                  fontWeight={fontWeight.REGULAR}
                  isEllipses
                />
              </div> : null}
            </div> : null}
          </div>
        </AccordionDetails>

        {/* detail comment section */}
        {showOperation && (
          <div className={classes.emailLogListCommentContainer}>
            <AddComment
              createCommentHandler={createCommentHandler}
              value={commentBody}
              setValue={setCommentBody}
              isLoading={createCommentLoading}
              commentsList={emailComments}
              onScrollHandler={() =>
                setEmailCommentsPage(emailCommentsPage + 1)
              }
              updateCommentHandler={updateEmailLogCommentHandler}
              deleteModalHandler={deleteEmailLogCommentModalHandler}
            />

            <DeleteCommentModal
              open={deleteCommentModalOpen}
              close={() => {
                setDeleteCommentModalOpen(false);
              }}
              clickedData={clickedCommentData}
              deleteButtonLoading={isDeleteCommentLoading}
              deleteAPIHandler={deleteEmailLogCommentHandler}
            />
          </div>
        )}
      </Accordion>

      {/* Delete Email log list modal. */}
      <CustomModal
        variant={"error"}
        close={() => setDeleteModal(false)}
        open={deleteModal}
        header={deleteModalHeader}
        footer={{
          left: deleteModalLeftFooter,
          right: deleteModalRightFooter,
        }}
      >
        <div className={classes.modalBody}>
          <TypographyInline
            size={"sm"}
            color={GREY.SECONDARY}
            fontWeight={fontWeight.REGULAR}
            label={STRINGS.DELETE_COMMENT_DESC}
          />
        </div>
      </CustomModal>
    </div>
  );
};

export default EmailSentList;
